import React, {
  InputHTMLAttributes,
  RefObject,
  useContext,
  useEffect,
  useRef,
} from 'react';
import c from 'classnames';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { ListItemContentProps } from './legos/ListItemContent';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './common.scss';

/*
  Read the comments below before reading this.

  Why all this instead of compartmentalizing the content props into a `contentProps` object? Because this is a case
  where the consumer doesn't need to know the internal workings of the component. In other odd cases, we pass through
  fields like `labelClassName` that may be passed down through a couple component levels. This inherently requires a
  knowledge of what's going on inside the component. We aren't doing that here. These are all important props that should
  be exposed at the base of the ListItem API and are inherent to what a ListItem is.

  The approach below keeps all the ListItemContentProps compartmentalized inside the ListItem code without requiring the user
  to pass in a separate object.
*/

/**
 * Each ListItem implementation requires the ListItemContent component. Separating out the props in each implementation
 * will get unwieldy and is prone to a lot of accidental errors or annoying component differences. So this variable keeps
 * of all the ListItemContent props that we pass through from each ListItem implementation as is.
 *
 * Note that it's possible for a prop to be used by this common wrapper _and_ by the ListItemContent (cf. `biggerPaddings`).
 */
const passThroughContentProps = [
  'label',
  'subLabel',
  'bottomSlot',
  'backgroundImage',
  'prefix',
  'prefixIcon',
  'suffix',
  'suffixIcon',
  'isLabelBold',
  'isDangerous',
  'biggerPaddings',
  'onLabelClick',
  'onPrefixClick',
  'allowTextWrap',
  // This cast converts the array from `string[]` to a union of string literals. Useful for keying into other type objects.
] as const;

type LimitedListItemContentProps = Pick<
  ListItemContentProps,
  (typeof passThroughContentProps)[number]
>;

/**
 * A collection of props that makes a ListItem implementation a ListItem. Have your new implementation's prop interface
 * extend this.
 */
export interface CommonListItemAPI
  extends LimitedListItemContentProps,
    Pick<InputHTMLAttributes<any>, 'disabled' | 'checked'> {
  compact?: boolean; // DS6.1.6
  biggerPaddings?: boolean; // DS6.1.10
  showSuffixOnlyOnHover?: boolean;
  fillContainerWidth?: boolean;
  focused?: boolean;
  className?: string;
  isHighlighted?: boolean;
  // TODO: Figure out a way to remove onContentClick. Used in studio.
  onContentClick?: (ev: React.MouseEvent) => void;
}

const apiDefaults: Partial<CommonListItemAPI> = {};

/**
 * These are present in the user-facing API, but aren't used in disparate ListItem implementations. They're mostly used
 * in shared ListItem code. Like when we fetch the initial root component `className`.
 */
const commonApiFieldsToStrip = [
  'compact',
  'biggerPaddings',
  'showSuffixOnlyOnHover',
  'fillContainerWidth',
  'focused',
  'isHighlighted',
] as const;

export enum EffectStrength {
  None = 'none',
  Weak = 'weak',
  Strong = 'strong',
}

interface InteractionEffects {
  hover: EffectStrength;
  checked: EffectStrength;
}

const interactionEffectDefaults: InteractionEffects = {
  hover: EffectStrength.Weak,
  checked: EffectStrength.None,
};

/**
 * Fetchs all the {@link ListItemContentProps} out of the main props object so that it's easy to pass them along to the
 * ListItemContent component.
 */
function getContentProps<T extends CommonListItemAPI>(
  propsWithoutDefaults: T
): ListItemContentProps {
  const props = { ...apiDefaults, ...propsWithoutDefaults };
  return {
    ...pick(props, passThroughContentProps),
    onClick: props.onContentClick,
    isDim: props.disabled,
  };
}

/**
 * Omits all {@link ListItemContentProps} from the main props object. Useful in cases where we pass the `{...rest}` to the
 * component wrapping our ListItemContent.
 *
 * e.g.
 * ```
 *  [...]
 *  const radioProps = getRootProps(props)
 *  return (
 *    <RadioButton {...radioProps}>
 *      <ListItemContent {...contentProps}/>
 *    </RadioButton>
 *  )
 * ```
 */
function getRootProps<T extends CommonListItemAPI>(
  propsWithoutDefaults: T,
  interactionEffects: InteractionEffects
): Omit<
  T,
  | (typeof passThroughContentProps)[number]
  | (typeof commonApiFieldsToStrip)[number]
> {
  const props = { ...apiDefaults, ...propsWithoutDefaults };

  return {
    ...omit(props, [
      // These are only relevant for the content component, so the root component doesn't
      // need to worry about it.
      ...passThroughContentProps,
      // These are only relevant to common ListItem code, like the `getRootClassNames` function.
      // Removing them keeps us from accidentally passing an invalid prop to a DOM element
      ...commonApiFieldsToStrip,
    ]),
    className: getRootClassNames(props, interactionEffects),
  };
}

function getRootClassNames<T extends CommonListItemAPI>(
  {
    disabled,
    checked,
    compact,
    biggerPaddings,
    fillContainerWidth,
    showSuffixOnlyOnHover,
    isHighlighted,
    className,
  }: T,
  interactionEffects: InteractionEffects
) {
  return c(
    s['root'],
    // Using two interactionEffects.hover classnames here to make the css rules easier to work with.
    interactionEffects.hover !== EffectStrength.None && s['has-hover-effect'],
    interactionEffects.hover !== EffectStrength.None &&
      s[`hover-effect-${interactionEffects.hover}`],
    interactionEffects.checked !== EffectStrength.None &&
      checked &&
      s[`checked-effect-${interactionEffects.checked}`],
    disabled && s['disabled'],
    compact && s['compact'],
    biggerPaddings && s['padded'],
    fillContainerWidth && s['fill-container-width'],
    showSuffixOnlyOnHover && s['suffix-on-hover'],
    isHighlighted && s['highlighted'],
    className
  );
}

/**
 * Scrolls to the element that the supplied `ref` points to, assuming that the supplied props object has
 * a `focused` prop set to true.
 */
function useFocusedPropHandler<
  /**
   * Why this weird type? Because refs are complicated and if we need access to input component
   * label+input html tag refs, we need to use `useImperativeHandle` to route the `focus` function
   * to the correct element.
   */
  T extends RefObject<{ focus: () => void } & any>,
  P extends CommonListItemAPI,
>(ref: T, props: P) {
  useEffect(() => {
    if (ref.current && props.focused) {
      ref.current.focus({ preventScroll: true });
    }
  }, [ref, props.focused]);
}

/**
 * Takes a ref + props type, a props object, and returns a set of variables for easy use inside any ListItem implementation.
 */
export function useListItem<
  T extends { focus: () => void },
  P extends CommonListItemAPI,
>(props: P, interactionEffects: Partial<InteractionEffects> = {}) {
  const isMobile = useContext(MobileContext);
  const contentProps = getContentProps(props);
  const rootProps = getRootProps(props, {
    ...interactionEffectDefaults,
    // disable hover effects on mobile
    ...(isMobile
      ? { hover: EffectStrength.None, checked: interactionEffects.checked }
      : interactionEffects),
  });

  const ref = useRef<T | null>(null);

  useFocusedPropHandler(ref, props);

  return {
    /**
     * Props that -- in most cases -- should be spread onto the root component of your ListItem implementation.
     */
    rootProps,
    /**
     * Props that should be spread onto the content component of your ListItem implementation.
     */
    contentProps,
    // `ref` is separate from `remainingProps` for typescript reasons. Could maybe be rolled into `remainingProps` with the right types if you want
    // to dive into it.
    /**
     * Plop this on your root tag.
     */
    ref,
  };
}
